import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Entity } from '@models/companies/entity';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { SecurityChange } from '@models/documents/security-change';
import { IndividualData } from '@models/individuals/individual-data';
import { SecuritySourcingResult } from '@models/requests/sourcing-result';
import { SecurityTransaction } from "@models/security-transactions/security-transaction";
import { SecurityRegistryRecord } from '@models/shares/security-registry-record';
import {
  calculateSecurityTransactionsBalance
} from "@pages/shares/components/shares-page/utils/calculate-security-transactions-balance";
import BaseApiService from "@shared/services/base/base-api.service";
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SecurityApiService extends BaseApiService {
  getSecurityRegistryRecords(id: string, groupByAsic = true, excludedDocumentId = '', applyIncompleteDocuments = true, asOfDate: Date | null = null, excludedTransactionsIds: string[] | null = null): Observable<SecurityRegistryRecord[]> {
    const options = {
      params: {
        groupByAsic,
        applyIncompleteDocuments,
      }
    };

    if (asOfDate) options.params['asOfDate'] = formatDate(asOfDate, 'yyyy-MM-dd', 'en-US');
    if (excludedTransactionsIds) options.params['excludedTransactionsIds'] = excludedTransactionsIds;
    if (excludedDocumentId) options.params['excludedDocumentId'] = excludedDocumentId;

    return this.api.get<SecurityRegistryRecord[]>(`${ this.apiUrl }/securities/${ id }/records`, options)
      .pipe(map((data: SecurityRegistryRecord[]) => data.map(record => new SecurityRegistryRecord(record))));
  }

  getSecurityRegistry(id: string, groupByAsic = true, excludedDocumentId = '', applyIncompleteDocuments = true, asOfDate: Date | null = null): Observable<SecuritySourcingResult> {
    const options = {
      params: {
        excludedDocumentId,
        groupByAsic,
        applyIncompleteDocuments,
        asOfDate: asOfDate ? formatDate(asOfDate, 'yyyy-MM-dd', 'en-US') : ''
      }
    };

    return this.api.get<SecuritySourcingResult>(`${ this.apiUrl }/securities/${ id }/`, options)
      .pipe(map((data: SecuritySourcingResult) => new SecuritySourcingResult(data)));
  }

  getSecurityEstimate<T extends Entity>(entityId: string, change: SecurityChange<T>[], excludedDocumentId = ''): Observable<SecurityRegistryRecord[]> {
    const options = { params: { excludedDocumentId } };
    return this.api.post<SecurityRegistryRecord[]>(`${ this.apiUrl }/securities/${ entityId }/estimate`, change, options)
      .pipe(map((data) => data.map(record => new SecurityRegistryRecord(record))));
  }

  getSecurityTransactions(companyId: string): Observable<SecurityTransaction[]> {
    return this.api.get<SecurityTransaction[]>(`${ this.apiUrl }/securities/${ companyId }/transactions`)
      .pipe(map((data) => data.map((record) => new SecurityTransaction(record))))
      .pipe(map((data) => calculateSecurityTransactionsBalance(data, false)));
  }

  updateBeneficialOwner(relationshipId: string, beneficialOwnerName: string) {
    return this.api.post<void>(`${ this.apiUrl }/securities/${ relationshipId }/beneficial-owner`, `"${ beneficialOwnerName }"`, { headers: { 'Content-Type': 'application/json' } });
  }

  updateAuthSignatories(relationshipId: string, newAuthSignatories: IndividualData[]) {
    return this.api.post<void>(`${ this.apiUrl }/securities/${ relationshipId }/auth-signatories`, newAuthSignatories, { headers: { 'Content-Type': 'application/json' } });
  }

  getNextCertificateNumber(entityId: string): Observable<string> {
    return this.api.get<string>(`${ this.apiUrl }/securities/${ entityId }/certificate/next`);
  }

  checkCertificateExists(entityId: string, certNumber: string): Observable<boolean> {
    return this.api.head(`${ this.apiUrl }/securities/${ entityId }/certificate/${ certNumber }`, { observe: 'response' })
      .pipe(map(response => response.status === 200));
  }

  addHistoricalTransaction(entityId: string, change: EntityChangeData) {
    return this.api.post<void>(`${ this.apiUrl }/securities/${ entityId }/transactions`, change, { headers: { 'Content-Type': 'application/json' } });
  }

  deleteSecurityTransaction(entityId: string, transactionId: string): Observable<void> {
    return this.api.delete<void>(`${ this.apiUrl }/securities/${ entityId }/transactions/${ transactionId }`);
  }

  getTransactionChange(entityId: string, transactionId: string): Observable<EntityChangeData> {
    return this.api.get<EntityChangeData>(`${ this.apiUrl }/securities/${ entityId }/transactions/${ transactionId }/change`);
  }

  updateTransaction(entityId: string, change: EntityChangeData): Observable<void> {
    return this.api.put<void>(`${ this.apiUrl }/securities/${ entityId }/transactions`, change);
  }
}
